import { Injectable, signal } from '@angular/core';
import { ThemeConfiguration, ThemeDefinition, REQUIRED_COLOR_CATEGORIES, STORAGE_KEYS } from './theme.models';
import { DEFAULT_THEME, BRAND_THEME } from './theme.constants';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** Signal holding the active theme identifier */
  readonly activeTheme = signal<string>('default');

  /** All available predefined themes */
  readonly availableThemes: ReadonlyArray<ThemeDefinition> = [DEFAULT_THEME, BRAND_THEME];

  constructor() {
    this.restoreTheme();
  }

  /** Apply a theme by identifier. Falls back to default if invalid. */
  applyTheme(themeId: string): void {
    const theme = this.availableThemes.find(t => t.id === themeId);
    if (!theme) {
      this.applyTheme('default');
      return;
    }

    // For now, use light config (DarkModeService will interact with this later)
    const config = theme.light;

    if (!this.validateConfig(config)) {
      this.applyTheme('default');
      return;
    }

    this.activeTheme.set(theme.id);
    this.setCssProperties(config);
    this.persistTheme(theme.id);
  }

  /** Validate that a ThemeConfiguration contains all required color categories */
  validateConfig(config: ThemeConfiguration): boolean {
    if (!config || typeof config !== 'object') {
      return false;
    }

    return REQUIRED_COLOR_CATEGORIES.every(
      category => category in config && typeof config[category] === 'string' && config[category].length > 0
    );
  }

  /** Restore theme from localStorage on init */
  private restoreTheme(): void {
    let storedThemeId: string | null = null;

    try {
      storedThemeId = localStorage.getItem(STORAGE_KEYS.THEME);
    } catch {
      // localStorage unavailable — fall back to default
    }

    if (storedThemeId && this.availableThemes.some(t => t.id === storedThemeId)) {
      this.applyTheme(storedThemeId);
    } else {
      this.applyTheme('default');
    }
  }

  /** Set CSS custom properties on document.documentElement */
  setCssProperties(config: ThemeConfiguration): void {
    const root = document.documentElement;

    for (const key of REQUIRED_COLOR_CATEGORIES) {
      root.style.setProperty(`--color-${key}`, config[key]);
    }
  }

  /** Persist theme ID to localStorage (graceful failure) */
  private persistTheme(themeId: string): void {
    try {
      localStorage.setItem(STORAGE_KEYS.THEME, themeId);
    } catch {
      // localStorage unavailable — silently ignore
    }
  }
}
